import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasteableInput } from "@/components/PasteableInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, MagnifyingGlass, PencilSimple, Trash, Phone, MapPin, Storefront,
  Receipt, X, Buildings, EnvelopeSimple, ShoppingCart,
} from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";

const TYPES = [
  { v: "final",     label: "Cliente Final", color: "#52525B" },
  { v: "reseller",  label: "Revendedor",    color: "#7E57C2" },
  { v: "wholesale", label: "Mayorista",     color: "#FF7043" },
  { v: "distributor", label: "Distribuidor", color: "#0EA5E9" },
];

const EMPTY = {
  name: "", business_name: "", display_name: "", city: "", address: "",
  phone: "", email: "", currency: "MXN", client_type: "final", notes: "", crm_stage: "lead",
};

const fmt = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MONTHS_ES = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
const monthLabel = (ym) => { const [y, m] = ym.split("-"); return `${MONTHS_ES[parseInt(m,10)-1]} ${y.slice(2)}`; };

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const [history, setHistory] = useState(null);
  const [activeMonth, setActiveMonth] = useState(null);

  const load = async () => setClients((await api.get("/clients")).data);
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...EMPTY, ...c }); setShowForm(true); };

  const openDetail = async (c) => {
    setDetail(c); setHistory(null); setActiveMonth(null);
    try { const { data } = await api.get(`/clients/${c.id}/history`); setHistory(data); }
    catch { toast.error("Error cargando historial"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.put(`/clients/${editing.id}`, form);
      else await api.post("/clients", form);
      toast.success(editing ? "Cliente actualizado" : "Cliente creado");
      setShowForm(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try { await api.delete(`/clients/${toDelete.id}`); toast.success("Cliente eliminado"); load(); }
    catch { toast.error("Error"); }
    finally { setToDelete(null); }
  };

  const filtered = useMemo(() => clients.filter((c) => {
    if (typeFilter === "online_store") {
      if (!c.purchased_online) return false;
    } else if (typeFilter !== "all" && (c.client_type || "final") !== typeFilter) {
      return false;
    }
    if (q.trim()) {
      const s = q.toLowerCase();
      return [c.name, c.business_name, c.display_name, c.city, c.phone, c.address]
        .some((x) => (x || "").toLowerCase().includes(s));
    }
    return true;
  }), [clients, q, typeFilter]);

  const onlineBuyersCount = useMemo(
    () => clients.filter((c) => c.purchased_online).length,
    [clients],
  );

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">DIRECTORIO</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight">Clientes</h1>
          <p className="text-zinc-500 mt-1 text-sm">
            {clients.length} registrados · {filtered.length} visibles
            {onlineBuyersCount > 0 && (
              <> · <span className="text-[#009EE3] font-semibold" data-testid="online-buyers-count">{onlineBuyersCount} compraron por la tienda</span></>
            )}
          </p>
        </div>
        <Button onClick={openNew} data-testid="new-client-btn" className="bg-[#002FA7] hover:bg-[#002277] text-white gap-2">
          <Plus size={16} weight="bold" /> Nuevo cliente
        </Button>
      </header>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Buscar por nombre, negocio, ciudad…" className="pl-9" data-testid="client-search" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-mono-data uppercase tracking-wider text-zinc-500">Tipo:</span>
          {[{v:"all",label:"Todos",color:"#002FA7"}, ...TYPES, {v:"online_store",label:"Tienda online",color:"#009EE3"}].map((t) => (
            <button
              key={t.v}
              onClick={()=>setTypeFilter(t.v)}
              data-testid={`type-filter-${t.v}`}
              className={`px-3 py-1 text-xs font-medium rounded-sm border transition-all ${
                typeFilter===t.v ? "border-transparent text-white" : "bg-white border-zinc-200 text-zinc-600 hover:border-zinc-400"
              }`}
              style={typeFilter===t.v ? { background: t.color } : {}}
            >{t.label}{t.v === "online_store" && onlineBuyersCount > 0 ? ` (${onlineBuyersCount})` : ""}</button>
          ))}
        </div>
      </div>

      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="clients-table">
          <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3">Cliente</th>
              <th className="text-left px-4 py-3">Negocio</th>
              <th className="text-left px-4 py-3">Tipo</th>
              <th className="text-left px-4 py-3">Ciudad</th>
              <th className="text-left px-4 py-3">Teléfono</th>
              <th className="text-left px-4 py-3">Moneda</th>
              <th className="text-right px-4 py-3 w-32">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-16 text-center text-zinc-400 dotted-bg">Sin clientes</td></tr>
            )}
            {filtered.map((c) => {
              const type = TYPES.find((t)=>t.v===(c.client_type||"final")) || TYPES[0];
              return (
                <tr key={c.id} className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer" onClick={()=>openDetail(c)} data-testid={`client-row-${c.id}`}>
                  <td className="px-4 py-3 font-medium">
                    <div className="flex items-center gap-2">
                      <span>{c.display_name || c.name}</span>
                      {c.purchased_online && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded bg-[#009EE3]/10 text-[#0084c2]"
                          title={c.online_purchases_count > 1 ? `${c.online_purchases_count} compras en la tienda online` : "Compró por la tienda online"}
                          data-testid={`badge-online-${c.id}`}
                        >
                          <ShoppingCart size={10} weight="fill" />
                          Tienda{c.online_purchases_count > 1 ? ` ×${c.online_purchases_count}` : ""}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-zinc-700">{c.business_name || "—"}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-semibold px-2 py-1 rounded-sm" style={{ background: type.color+"20", color: type.color }}>{type.label}</span>
                  </td>
                  <td className="px-4 py-3 text-zinc-700">{c.city || "—"}</td>
                  <td className="px-4 py-3 text-zinc-700 font-mono-data text-xs">{c.phone || "—"}</td>
                  <td className="px-4 py-3"><span className="text-xs font-mono-data px-2 py-1 bg-zinc-100 rounded">{c.currency}</span></td>
                  <td className="px-4 py-3" onClick={(e)=>e.stopPropagation()}>
                    <div className="flex justify-end gap-1">
                      <button onClick={()=>openEdit(c)} title="Editar" data-testid={`edit-client-${c.id}`} className="p-2 hover:bg-zinc-200 rounded">
                        <PencilSimple size={15} />
                      </button>
                      <button onClick={()=>setToDelete(c)} title="Eliminar" data-testid={`del-client-${c.id}`} className="p-2 hover:bg-red-100 text-[#E0000F] rounded">
                        <Trash size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit/Create form */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="client-form-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editing ? "Editar cliente" : "Nuevo cliente"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider">Nombre *</Label>
                <Input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} required data-testid="client-name" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Negocio</Label>
                <Input value={form.business_name} onChange={(e)=>setForm({...form,business_name:e.target.value})} data-testid="client-business" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Aparecer en nota como</Label>
                <Select
                  value={
                    !form.display_name ? "auto"
                    : form.display_name === form.name ? "name"
                    : form.display_name === form.business_name ? "business"
                    : "custom"
                  }
                  onValueChange={(v) => {
                    if (v === "auto") setForm({ ...form, display_name: "" });
                    else if (v === "name") setForm({ ...form, display_name: form.name });
                    else if (v === "business") setForm({ ...form, display_name: form.business_name });
                    else if (v === "custom") setForm({ ...form, display_name: form.display_name || form.name || form.business_name || "" });
                  }}
                >
                  <SelectTrigger className="mt-1" data-testid="client-display-mode">
                    <SelectValue placeholder="Elegir..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Automático (negocio si existe, si no nombre)</SelectItem>
                    <SelectItem value="name" disabled={!form.name}>
                      Nombre{form.name ? `: ${form.name}` : " (escribe el nombre primero)"}
                    </SelectItem>
                    <SelectItem value="business" disabled={!form.business_name}>
                      Negocio{form.business_name ? `: ${form.business_name}` : " (escribe el negocio primero)"}
                    </SelectItem>
                    <SelectItem value="custom">Personalizado…</SelectItem>
                  </SelectContent>
                </Select>
                {/* Custom input shown only when "Personalizado" is active */}
                {form.display_name && form.display_name !== form.name && form.display_name !== form.business_name && (
                  <Input
                    value={form.display_name}
                    onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                    placeholder="Texto libre"
                    className="mt-2"
                    data-testid="client-display-custom"
                  />
                )}
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Ciudad</Label>
                <Input value={form.city} onChange={(e)=>setForm({...form,city:e.target.value})} data-testid="client-city" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Teléfono</Label>
                <PasteableInput value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} data-testid="client-phone" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Email</Label>
                <PasteableInput type="email" value={form.email || ""} onChange={(e)=>setForm({...form,email:e.target.value})} data-testid="client-email" className="mt-1" placeholder="cliente@correo.com" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider">Dirección</Label>
                <PasteableInput value={form.address} onChange={(e)=>setForm({...form,address:e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Moneda preferida</Label>
                <Select value={form.currency} onValueChange={(v)=>setForm({...form,currency:v})}>
                  <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MXN">MXN (Pesos)</SelectItem>
                    <SelectItem value="USD">USD (Dólares)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Tipo de cliente</Label>
                <Select value={form.client_type || "final"} onValueChange={(v)=>setForm({...form,client_type:v})}>
                  <SelectTrigger className="mt-1" data-testid="client-type"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {TYPES.map((t)=><SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider">Notas</Label>
                <Textarea value={form.notes} onChange={(e)=>setForm({...form,notes:e.target.value})} rows={2} className="mt-1" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={()=>setShowForm(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-[#002FA7] hover:bg-[#002277] text-white" data-testid="client-submit">
                {saving ? "Guardando…" : (editing ? "Actualizar" : "Crear")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o)=>{ if (!o) setToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar cliente?</AlertDialogTitle>
            <AlertDialogDescription>Se eliminará <b>{toDelete?.name}</b>. Las notas de venta históricas se conservan.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-[#E0000F] hover:bg-[#b8000c] text-white">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Detail dialog (Zoho-style) */}
      <Dialog open={!!detail} onOpenChange={(o)=>{ if (!o) { setDetail(null); setHistory(null); setActiveMonth(null); } }}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto p-0" data-testid="client-detail-dialog">
          {detail && (() => {
            const type = TYPES.find((t)=>t.v===(detail.client_type||"final")) || TYPES[0];
            return (
              <>
                <div className="border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
                  <div>
                    <div className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500">CLIENTE</div>
                    <h2 className="font-heading text-3xl font-extrabold uppercase tracking-tight" data-testid="client-detail-name">{detail.display_name || detail.name}</h2>
                    <span className="inline-block mt-1 text-xs font-semibold px-2 py-1 rounded-sm" style={{ background: type.color+"20", color: type.color }}>{type.label}</span>
                  </div>
                  <Button variant="outline" size="sm" onClick={()=>{ openEdit(detail); setDetail(null); }} className="gap-1">
                    <PencilSimple size={14} /> Editar
                  </Button>
                </div>

                <div className="grid md:grid-cols-[300px_1fr] gap-0">
                  <aside className="border-r border-zinc-200 p-6 space-y-5 bg-zinc-50/40">
                    <div>
                      <div className="text-xs font-mono-data uppercase tracking-wider text-zinc-500 mb-2">DIRECCIÓN</div>
                      <div className="space-y-1 text-sm">
                        <div className="flex items-start gap-2"><MapPin size={14} className="text-zinc-400 mt-0.5" /><span>{detail.address || <span className="text-zinc-400">Sin dirección</span>}</span></div>
                        <div className="flex items-start gap-2"><Buildings size={14} className="text-zinc-400 mt-0.5" /><span>{detail.city || <span className="text-zinc-400">Sin ciudad</span>}</span></div>
                      </div>
                    </div>

                    <div>
                      <div className="text-xs font-mono-data uppercase tracking-wider text-zinc-500 mb-2">CONTACTO</div>
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2"><Phone size={14} className="text-zinc-400" /><span>{detail.phone || <span className="text-zinc-400">Sin teléfono</span>}</span></div>
                        {detail.business_name && (
                          <div className="flex items-center gap-2"><Storefront size={14} className="text-zinc-400" /><span>{detail.business_name}</span></div>
                        )}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs font-mono-data uppercase tracking-wider text-zinc-500 mb-2">OTROS DETALLES</div>
                      <dl className="text-sm space-y-2">
                        <div className="flex justify-between"><dt className="text-zinc-500">Tipo</dt><dd className="font-medium">{type.label}</dd></div>
                        <div className="flex justify-between"><dt className="text-zinc-500">Moneda</dt><dd className="font-mono-data">{detail.currency}</dd></div>
                        <div className="flex justify-between"><dt className="text-zinc-500">Alta</dt><dd className="text-xs">{new Date(detail.created_at).toLocaleDateString("es-MX")}</dd></div>
                      </dl>
                    </div>

                    {detail.notes && (
                      <div>
                        <div className="text-xs font-mono-data uppercase tracking-wider text-zinc-500 mb-2">NOTAS</div>
                        <p className="text-sm text-zinc-700">{detail.notes}</p>
                      </div>
                    )}

                    {(detail.client_type === "reseller" || detail.client_type === "wholesale" || detail.client_type === "distributor") && (
                      <PortalAccessPanel client={detail} onUpdated={async ()=>{
                        const { data } = await api.get(`/clients`);
                        const fresh = data.find(c => c.id === detail.id);
                        if (fresh) setDetail(fresh);
                      }} />
                    )}
                  </aside>

                  <section className="p-6 space-y-5">
                    {!history ? (
                      <div className="py-16 text-center text-zinc-400">Cargando historial…</div>
                    ) : (
                      <>
                        <div className="grid grid-cols-3 gap-3">
                          <div className="border border-zinc-200 p-4">
                            <div className="text-xs uppercase font-mono-data tracking-wider text-zinc-500">Pedidos</div>
                            <div className="numeric text-3xl font-bold mt-1" data-testid="kpi-orders">{history.total_orders}</div>
                          </div>
                          <div className="border border-zinc-200 p-4">
                            <div className="text-xs uppercase font-mono-data tracking-wider text-zinc-500">Comprado MXN</div>
                            <div className="numeric text-xl font-bold mt-1 text-[#00873D]" data-testid="kpi-total-mxn">MX${fmt(history.total_revenue_mxn)}</div>
                          </div>
                          <div className="border border-zinc-200 p-4">
                            <div className="text-xs uppercase font-mono-data tracking-wider text-zinc-500">Comprado USD</div>
                            <div className="numeric text-xl font-bold mt-1">${fmt(history.total_revenue_usd)}</div>
                          </div>
                        </div>

                        <div>
                          <div className="text-xs uppercase font-mono-data tracking-wider text-zinc-500 mb-2">INGRESOS · CLIC EN UNA BARRA PARA DETALLE</div>
                          {history.by_month.length === 0 ? (
                            <div className="dotted-bg py-12 text-center text-zinc-400 text-sm">Aún sin compras</div>
                          ) : (
                            <ResponsiveContainer width="100%" height={240}>
                              <BarChart data={history.by_month} onClick={(e)=>{ if (e?.activePayload?.[0]) setActiveMonth(e.activePayload[0].payload.month); }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
                                <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 11 }} />
                                <Tooltip formatter={(v, n, p) => [`${p.payload.currency === "USD" ? "$" : "MX$"}${fmt(v)}`, "Total"]} labelFormatter={monthLabel} />
                                <Bar dataKey="total" fill="#00873D" radius={[4,4,0,0]} cursor="pointer" />
                              </BarChart>
                            </ResponsiveContainer>
                          )}
                        </div>

                        {activeMonth && (() => {
                          const m = history.by_month.find((x)=>x.month===activeMonth);
                          if (!m) return null;
                          return (
                            <div className="border border-[#002FA7]/30 bg-[#002FA7]/5 p-4 space-y-2">
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="text-xs uppercase font-mono-data tracking-wider text-[#002FA7]">DETALLE DEL MES</div>
                                  <h4 className="font-heading text-lg font-bold">{monthLabel(activeMonth)} · {m.count} {m.count===1?"nota":"notas"}</h4>
                                </div>
                                <button onClick={()=>setActiveMonth(null)} className="text-zinc-400 hover:text-zinc-700"><X size={18}/></button>
                              </div>
                              <table className="w-full text-sm bg-white border border-zinc-200">
                                <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
                                  <tr>
                                    <th className="text-left px-3 py-2">Folio</th>
                                    <th className="text-left px-3 py-2">Fecha</th>
                                    <th className="text-right px-3 py-2">Total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {m.sale_ids.map((s)=>(
                                    <tr key={s.id} className="border-t border-zinc-100">
                                      <td className="px-3 py-2 font-mono-data font-semibold">#{String(s.folio).padStart(6,"0")}</td>
                                      <td className="px-3 py-2 text-zinc-600 text-xs">{new Date(s.created_at).toLocaleString("es-MX")}</td>
                                      <td className="px-3 py-2 text-right numeric font-semibold text-[#002FA7]">{s.currency==="USD"?"$":"MX$"}{fmt(s.total)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          );
                        })()}

                        <div>
                          <div className="text-xs uppercase font-mono-data tracking-wider text-zinc-500 mb-2 flex items-center gap-2">
                            <Receipt size={14} /> TODAS LAS NOTAS ({history.sales.length})
                          </div>
                          {history.sales.length === 0 ? (
                            <div className="dotted-bg py-8 text-center text-zinc-400 text-xs">Sin notas</div>
                          ) : (
                            <table className="w-full text-sm border border-zinc-200">
                              <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
                                <tr>
                                  <th className="text-left px-3 py-2">Folio</th>
                                  <th className="text-left px-3 py-2">Fecha</th>
                                  <th className="text-right px-3 py-2">Items</th>
                                  <th className="text-right px-3 py-2">Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {history.sales.map((s)=>(
                                  <tr key={s.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                                    <td className="px-3 py-2 font-mono-data font-semibold">#{String(s.folio).padStart(6,"0")}</td>
                                    <td className="px-3 py-2 text-zinc-600 text-xs">{new Date(s.created_at).toLocaleString("es-MX")}</td>
                                    <td className="px-3 py-2 text-right numeric">{s.items?.length || 0}</td>
                                    <td className="px-3 py-2 text-right numeric font-semibold text-[#002FA7]">{s.currency==="USD"?"$":"MX$"}{fmt(s.total)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </>
                    )}
                  </section>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PortalAccessPanel({ client, onUpdated }) {
  const [discount, setDiscount] = useState(client.portal_discount_pct || 0);
  const [tempPwd, setTempPwd] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const enabled = !!client.portal_enabled;
  // The plaintext password we keep on the client doc (only present while the client
  // hasn't changed it themselves). null/undefined once the client sets their own.
  const storedPwd = client.portal_password_plain || null;

  const enable = async () => {
    if (!client.email) { toast.error("Primero edita el cliente y agrega un email"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/clients/${client.id}/portal/enable`, { discount_pct: parseFloat(discount) || 0 });
      setTempPwd(data.temp_password);
      toast.success("Portal habilitado");
      onUpdated();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };
  const resetPwd = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/clients/${client.id}/portal/reset`);
      setTempPwd(data.temp_password);
      toast.success("Contraseña reseteada");
      onUpdated();
    } catch (err) { toast.error("Error"); }
    finally { setSaving(false); }
  };
  const updateDiscount = async () => {
    setSaving(true);
    try {
      await api.patch(`/clients/${client.id}/portal`, { discount_pct: parseFloat(discount) || 0 });
      toast.success("Descuento actualizado");
      onUpdated();
    } catch { toast.error("Error"); }
    finally { setSaving(false); }
  };
  const disable = async () => {
    setSaving(true);
    try {
      await api.patch(`/clients/${client.id}/portal`, { portal_enabled: false });
      toast.success("Portal deshabilitado");
      onUpdated();
    } catch { toast.error("Error"); }
    finally { setSaving(false); }
  };
  const copy = async (txt) => {
    try { await navigator.clipboard.writeText(txt); toast.success("Copiado"); }
    catch (err) { console.warn("Clipboard write failed:", err); toast.error("No se pudo copiar"); }
  };

  return (
    <div className="border-t border-zinc-200 pt-5 mt-4">
      <div className="text-xs font-mono-data uppercase tracking-wider text-[#002FA7] mb-2">ACCESO AL PORTAL</div>
      {!enabled ? (
        <div className="space-y-2">
          <p className="text-xs text-zinc-500">Habilita acceso para que el cliente entre al portal y arme su carrito.</p>
          <div className="flex items-center gap-2">
            <input type="number" value={discount} onChange={(e)=>setDiscount(e.target.value)} className="w-20 text-xs border border-zinc-300 rounded px-2 py-1 numeric" min="0" max="100" step="0.5" />
            <span className="text-xs text-zinc-500">% descuento</span>
            <Button size="sm" onClick={enable} disabled={saving} className="bg-[#002FA7] hover:bg-[#002277] text-white">Habilitar</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="text-xs space-y-2">
            <div className="flex items-center gap-2">
              <b className="w-20">Email:</b>
              <code className="flex-1 text-[11px] bg-zinc-100 px-1.5 py-0.5 font-mono-data" data-testid="portal-cred-email">{client.portal_email}</code>
              <button type="button" onClick={()=>copy(client.portal_email)} className="text-[11px] text-[#002FA7] hover:underline" title="Copiar email">Copiar</button>
            </div>
            {storedPwd ? (
              <div className="flex items-center gap-2">
                <b className="w-20">Contraseña:</b>
                <code className="flex-1 text-[11px] bg-zinc-100 px-1.5 py-0.5 font-mono-data tracking-wider" data-testid="portal-cred-password">
                  {showPwd ? storedPwd : "••••••••••"}
                </code>
                <button type="button" onClick={()=>setShowPwd(!showPwd)} className="text-[11px] text-[#002FA7] hover:underline" title={showPwd ? "Ocultar" : "Mostrar"} data-testid="portal-cred-toggle">
                  {showPwd ? "Ocultar" : "Ver"}
                </button>
                <button type="button" onClick={()=>copy(storedPwd)} className="text-[11px] text-[#002FA7] hover:underline" title="Copiar contraseña" data-testid="portal-cred-copy">Copiar</button>
              </div>
            ) : (
              <div className="flex items-start gap-2 text-[11px] text-zinc-500 bg-zinc-50 border border-zinc-200 px-2 py-1.5 rounded">
                <span className="font-bold">ℹ</span>
                <span>El cliente ya configuró su propia contraseña. Si la perdió, usa <b>Resetear contraseña</b> para generar una nueva.</span>
              </div>
            )}
            <div className="flex items-center gap-2"><b className="w-20">Descuento:</b>
              <input type="number" value={discount} onChange={(e)=>setDiscount(e.target.value)} className="w-16 text-xs border border-zinc-300 rounded px-2 py-1 numeric" min="0" max="100" step="0.5" />
              <span>%</span>
              <Button size="sm" variant="outline" onClick={updateDiscount} disabled={saving} className="h-6 px-2 text-[11px]">Guardar</Button>
            </div>
            {client.portal_must_change_password && <div className="text-[11px] text-[#FFC000]">⚠ Aún no cambia la contraseña temporal</div>}
          </div>
          {tempPwd && (
            <div className="bg-[#FFC000]/10 border border-[#FFC000]/40 p-3 rounded text-xs">
              <div className="font-bold mb-1">✓ Nueva contraseña generada:</div>
              <code className="block text-sm font-mono-data bg-white p-2 rounded border border-zinc-200">{tempPwd}</code>
              <button onClick={()=>copy(tempPwd)} className="mt-1 text-[11px] text-[#002FA7] hover:underline">Copiar</button>
            </div>
          )}
          <div className="flex gap-1 flex-wrap">
            <Button size="sm" variant="outline" onClick={resetPwd} disabled={saving} className="text-xs" data-testid="portal-cred-reset">Resetear contraseña</Button>
            <Button size="sm" variant="outline" onClick={disable} disabled={saving} className="text-xs text-[#E0000F] border-[#E0000F]/30">Deshabilitar</Button>
          </div>
        </div>
      )}
    </div>
  );
}
